import { CpmCheckbox } from '../../../components/design-system';
import type { StoreBrandListRow } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY } from '../copy';
import FilteredEmpty from './FilteredEmpty';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';

interface StoreBrandListProps {
    rows: StoreBrandListRow[];
    total: number;
    loadingMore?: boolean;
    moreFailed?: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    selectedIds?: string[];
    onToggle?: (id: string) => void;
    onOpen?: (row: StoreBrandListRow) => void;
    showTicks?: boolean;
    alreadyOnMode?: boolean;
    emptyUntilSearch?: boolean;
    searchActive?: boolean;
    filteredEmpty?: boolean;
    onClearFilters?: () => void;
}

export default function StoreBrandList({
    rows,
    total,
    loadingMore = false,
    moreFailed = false,
    hasMore,
    onLoadMore,
    selectedIds = [],
    onToggle,
    onOpen,
    showTicks = false,
    alreadyOnMode = false,
    emptyUntilSearch = false,
    searchActive = false,
    filteredEmpty = false,
    onClearFilters,
}: StoreBrandListProps) {
    if (emptyUntilSearch && !searchActive) {
        return (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <p className="m-0" style={{ fontSize: 14, fontWeight: 800 }}>
                    {REPORTS_COPY.EMPTY_BRANDS_TITLE}
                </p>
                <p
                    className="m-0"
                    style={{
                        marginTop: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {REPORTS_COPY.EMPTY_BRANDS_BODY}
                </p>
            </div>
        );
    }

    if (filteredEmpty) {
        return <FilteredEmpty onClear={onClearFilters ?? (() => undefined)} />;
    }

    return (
        <div>
            {searchActive ? (
                <p
                    className="m-0"
                    style={{
                        padding: '10px 16px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {total} store brands match · showing {rows.length}
                </p>
            ) : null}
            <div
                className="overflow-auto"
                style={{
                    maxHeight: 420,
                    border: '1px solid var(--cpm-border-default)',
                    borderRadius: 10,
                }}
            >
                {rows.map((row) => {
                    const selected = selectedIds.includes(row.storeBrandId);
                    return (
                        <div
                            key={row.storeBrandId}
                            className="flex items-center"
                            onClick={() => {
                                if (showTicks) onToggle?.(row.storeBrandId);
                                else onOpen?.(row);
                            }}
                            style={{
                                gap: 12,
                                padding: '11px 16px',
                                borderBottom: '1px solid var(--cpm-border-default)',
                                cursor: showTicks || onOpen ? 'pointer' : 'default',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--cpm-purple-tint-3)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            {showTicks ? (
                                <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                                    <CpmCheckbox
                                        checked={selected}
                                        onChange={() => onToggle?.(row.storeBrandId)}
                                    />
                                </span>
                            ) : null}
                            <div className="flex-1 min-w-0">
                                <div style={{ fontSize: 13.5, fontWeight: 800 }}>{row.name}</div>
                                <div
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {row.state ?? (row.mixedStates?.join(', ') || '—')}
                                    {typeof row.enabledCount === 'number'
                                        ? ` · ${row.enabledCount} on`
                                        : ''}
                                    {row.lastToggledAt
                                        ? ` · ${new Date(row.lastToggledAt).toLocaleDateString(
                                              'en-IN',
                                              {
                                                  day: 'numeric',
                                                  month: 'short',
                                              }
                                          )}`
                                        : typeof row.enabledCount === 'number' &&
                                            row.enabledCount === 0
                                          ? ` · ${REPORTS_COPY.NO_TOGGLE}`
                                          : ''}
                                </div>
                            </div>
                            {alreadyOnMode && row.alreadyOn != null ? (
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: row.alreadyOn
                                            ? 'var(--cpm-success-text)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {row.alreadyOn ? 'On' : 'Off'}
                                </span>
                            ) : null}
                            {showTicks && row.wouldAdd != null ? (
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: row.wouldAdd
                                            ? 'var(--cpm-brand-purple)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {row.wouldAdd ? 'Would add' : 'Already has these'}
                                </span>
                            ) : null}
                        </div>
                    );
                })}
                <InfiniteScrollSentinel
                    hasMore={hasMore}
                    loading={loadingMore}
                    error={moreFailed}
                    onLoadMore={onLoadMore}
                    showEnd={rows.length > 0}
                />
            </div>
        </div>
    );
}
